import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, isRef as _isRef, withCtx as _withCtx, createVNode as _createVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderList as _renderList, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, ref, watch } from "vue";
import { GroupableTableProps, TableGroupRow, TableRow } from "@/types";
import { useTableFilter, useVModel } from "@/hooks";
import GroupableTableRow from "./GroupableTableRow.vue";
import GroupableTableHeader from "./GroupableTableHeader.vue";
import DataTable from "./DataTable.vue";


/**
 * A table that can be grouped and expanded.
 */
const __default__ = {
  name: "GroupableTable",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    itemName: {},
    defaultGroupBy: {},
    defaultSortBy: {},
    defaultSortDesc: { type: Boolean },
    filterRow: { type: Function },
    columns: {},
    rows: {},
    rowKey: { type: [String, Function] },
    loading: { type: Boolean },
    expanded: {},
    customCells: {},
    expandable: { type: Boolean }
  },
  emits: ["update:expanded", "update:groupBy", "group:open", "group:close", "row-click"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { searchText, searchLabel, sortBy, sortDesc, filteredRows } =
  useTableFilter(props);

const groupBy = ref<string | undefined>(props.defaultGroupBy);

const expandedRows = useVModel(props, "expanded");

const customCellSlots = computed(() =>
  props.customCells
    ? props.customCells.map((name) => `body-cell-${String(name)}`)
    : []
);

/**
 * Applies sorting and grouping to filtered rows by adding header rows in between each group.
 */
const groupedRows = computed(() => {
  if (!groupBy.value) {
    return filteredRows.value;
  }

  const rowsByGroup: Record<string, TableGroupRow[]> = {};

  filteredRows.value.forEach((row) => {
    const group = String(row[groupBy.value || ""]);

    rowsByGroup[group] = [...(rowsByGroup[group] || []), row];
  });

  let sortIdx = 0;

  return Object.entries<TableGroupRow[]>(rowsByGroup).flatMap<
    TableGroupRow[],
    TableRow[]
  >(
    ([$groupValue, groupRows]) => [
      {
        id: `${groupBy.value}::${$groupValue}`,
        [String(props.rowKey)]: `${groupBy.value}::${$groupValue}`,
        $groupValue,
        $groupBy: groupBy.value,
        $groupRows: groupRows.length,
        $sortIdx: sortIdx++,
      },
      ...groupRows.map((row) => ({ ...row, $sortIdx: sortIdx++ })),
    ],
    []
  );
});

/**
 * Handles sorting of rows.
 * @param rows - The rows to sort.
 * @returns The sorted rows.
 */
function handleSort(rows: TableRow[]): TableRow[] {
  const getSortIndex = (row: TableRow): number =>
    typeof row.$sortIdx === "number" ? row.$sortIdx : 0;

  return rows.sort(
    (before, after) => getSortIndex(before) - getSortIndex(after)
  );
}

watch(
  () => groupBy.value,
  (group) => emit("update:groupBy", group)
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DataTable, {
    "sort-by": _unref(sortBy),
    "onUpdate:sortBy": _cache[6] || (_cache[6] = ($event: any) => (_isRef(sortBy) ? (sortBy).value = $event : null)),
    "sort-desc": _unref(sortDesc),
    "onUpdate:sortDesc": _cache[7] || (_cache[7] = ($event: any) => (_isRef(sortDesc) ? (sortDesc).value = $event : null)),
    expanded: _unref(expandedRows),
    "onUpdate:expanded": _cache[8] || (_cache[8] = ($event: any) => (_isRef(expandedRows) ? (expandedRows).value = $event : null)),
    "virtual-scroll": "",
    columns: props.columns,
    rows: groupedRows.value,
    "row-key": props.rowKey,
    loading: props.loading,
    sort: handleSort,
    separator: "horizontal"
  }, {
    top: _withCtx((scope) => [
      _createVNode(GroupableTableHeader, {
        "search-text": _unref(searchText),
        "onUpdate:searchText": _cache[0] || (_cache[0] = ($event: any) => (_isRef(searchText) ? (searchText).value = $event : null)),
        "group-by": groupBy.value,
        "onUpdate:groupBy": _cache[1] || (_cache[1] = ($event: any) => ((groupBy).value = $event)),
        "sort-by": _unref(sortBy),
        "onUpdate:sortBy": _cache[2] || (_cache[2] = ($event: any) => (_isRef(sortBy) ? (sortBy).value = $event : null)),
        "sort-desc": _unref(sortDesc),
        "onUpdate:sortDesc": _cache[3] || (_cache[3] = ($event: any) => (_isRef(sortDesc) ? (sortDesc).value = $event : null)),
        columns: _ctx.columns,
        "search-label": _unref(searchLabel),
        "in-fullscreen": scope.inFullscreen,
        onToggleFullscreen: scope.toggleFullscreen
      }, {
        "header-right": _withCtx(() => [
          _renderSlot(_ctx.$slots, "header-right")
        ]),
        "header-bottom": _withCtx(() => [
          _renderSlot(_ctx.$slots, "header-bottom")
        ]),
        _: 2
      }, 1032, ["search-text", "group-by", "sort-by", "sort-desc", "columns", "search-label", "in-fullscreen", "onToggleFullscreen"])
    ]),
    body: _withCtx((quasarProps) => [
      _createVNode(GroupableTableRow, {
        expand: quasarProps.expand,
        "onUpdate:expand": ($event: any) => ((quasarProps.expand) = $event),
        "quasar-props": quasarProps,
        columns: props.columns,
        row: quasarProps.row,
        expandable: props.expandable,
        onClick: ($event: any) => (emit('row-click', quasarProps.row)),
        "onGroup:open": _cache[4] || (_cache[4] = (by, val) => emit('group:open', by, val)),
        "onGroup:close": _cache[5] || (_cache[5] = (by, val) => emit('group:close', by, val))
      }, _createSlots({
        "body-expanded": _withCtx((scope) => [
          _renderSlot(_ctx.$slots, "body-expanded", _normalizeProps(_guardReactiveProps(scope)))
        ]),
        _: 2
      }, [
        _renderList(customCellSlots.value, (name) => {
          return {
            name: name,
            fn: _withCtx((scope) => [
              _renderSlot(_ctx.$slots, name, _normalizeProps(_guardReactiveProps(scope)))
            ])
          }
        })
      ]), 1032, ["expand", "onUpdate:expand", "quasar-props", "columns", "row", "expandable", "onClick"])
    ]),
    _: 3
  }, 8, ["sort-by", "sort-desc", "expanded", "columns", "rows", "row-key", "loading"]))
}
}

})